import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DISCIPLINES,
  CHALLENGES_PER_LEVEL,
  MAX_STARS_PER_CHALLENGE,
  LEVELS_PER_DISCIPLINE,
  type DisciplineSlug,
  type Subscription,
  canAccessLevel,
} from "@jumpbean/shared";
import { DisciplineLevelMap, type LevelProgress } from "../components";
import { useGameStore } from "../stores/gameStore";
import { useAppStore } from "../stores/appStore";
import { apiGet } from "../lib/api";

interface LevelData {
  id: string;
  levelNumber: number;
  name: string;
  badgeName: string;
}

interface LevelProgressData {
  levelId: string;
  completedChallenges: number;
  totalChallenges: number;
  starsEarned: number;
  hasBadge: boolean;
}

/**
 * DisciplinePage - Themed level map for a specific discipline.
 *
 * Features:
 * - Character-themed background and decorations
 * - Shows all 5 levels with locked/unlocked states
 * - Intro video preview at the top
 * - Badge display for completed levels
 * - Integrates with game store for progress data
 */
export function DisciplinePage() {
  const { discipline } = useParams<{ discipline: DisciplineSlug }>();
  const navigate = useNavigate();
  const { currentChild, cachedProgress, loadProgress } = useGameStore();
  const selectedChildId = useAppStore((state) => state.selectedChildId);

  // Get the child ID from either gameStore or appStore
  const childId = currentChild?.id || selectedChildId;

  const [levels, setLevels] = useState<LevelData[]>([]);
  const [progressData, setProgressData] = useState<Map<string, LevelProgressData>>(
    new Map()
  );
  const [isLoadingLevels, setIsLoadingLevels] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  // Get discipline data
  const disciplineData = discipline ? DISCIPLINES[discipline] : null;

  // Redirect to profiles if no child selected
  useEffect(() => {
    if (!childId) {
      navigate("/profiles");
    }
  }, [childId, navigate]);

  // Load levels, progress, and subscription
  useEffect(() => {
    if (!discipline || !childId) return;

    const loadData = async () => {
      setIsLoadingLevels(true);

      try {
        // Load progress if not already loaded
        if (cachedProgress.size === 0) {
          await loadProgress(childId);
        }

        // Fetch subscription status for level gating
        const subscriptionResponse = await apiGet<{ subscription: Subscription | null }>(
          "/api/billing/subscription"
        );
        if (subscriptionResponse.data) {
          setSubscription(subscriptionResponse.data.subscription);
        }

        // Fetch levels for this discipline (API returns discipline data with levels array)
        const levelsResponse = await apiGet<{
          id: string;
          slug: string;
          name: string;
          levels: LevelData[];
        }>(`/api/content/disciplines/${discipline}/levels`);

        if (levelsResponse.data?.levels) {
          setLevels(levelsResponse.data.levels);
        }

        // Fetch progress for each level
        const progressResponse = await apiGet<{
          childId: string;
          disciplines: Array<{
            slug: string;
            levels: Array<{
              levelId: string;
              levelNumber: number;
              completedChallenges: number;
              totalChallenges: number;
              starsEarned: number;
              hasBadge: boolean;
            }>;
          }>;
        }>(`/api/progress/${childId}/progress`);

        if (progressResponse.data) {
          const disciplineProgress = progressResponse.data.disciplines.find(
            (d) => d.slug === discipline
          );

          if (disciplineProgress) {
            const progressMap = new Map<string, LevelProgressData>();
            for (const level of disciplineProgress.levels) {
              progressMap.set(level.levelId, {
                levelId: level.levelId,
                completedChallenges: level.completedChallenges,
                totalChallenges: level.totalChallenges,
                starsEarned: level.starsEarned,
                hasBadge: level.hasBadge,
              });
            }
            setProgressData(progressMap);
          }
        }
      } catch (error) {
        console.error("Failed to load discipline data:", error);
      } finally {
        setIsLoadingLevels(false);
      }
    };

    loadData();
  }, [discipline, childId, loadProgress, cachedProgress.size]);

  // Build level progress data for display
  const buildLevelProgress = useCallback((): LevelProgress[] => {
    const maxStarsPerLevel = CHALLENGES_PER_LEVEL * MAX_STARS_PER_CHALLENGE;

    // Generate 5 levels with progress data
    return Array.from({ length: LEVELS_PER_DISCIPLINE }, (_, index) => {
      const levelNumber = index + 1;
      const level = levels.find((l) => l.levelNumber === levelNumber);
      const progress = level ? progressData.get(level.id) : null;

      // Check if user has subscription access to this level
      const hasSubscriptionAccess = canAccessLevel(subscription, levelNumber);

      // Level 1 is always unlocked
      // Other levels unlock when previous level is completed AND user has subscription access
      const previousLevel = levels.find((l) => l.levelNumber === levelNumber - 1);
      const previousProgress = previousLevel ? progressData.get(previousLevel.id) : null;
      const previousLevelCompleted =
        levelNumber === 1 ||
        (previousProgress?.completedChallenges ?? 0) >= CHALLENGES_PER_LEVEL;

      const isUnlocked = previousLevelCompleted && hasSubscriptionAccess;

      // Determine lock reason for UI messaging
      let lockReason: "previous_incomplete" | "subscription_required" | undefined;
      if (!isUnlocked) {
        if (!hasSubscriptionAccess) {
          lockReason = "subscription_required";
        } else if (!previousLevelCompleted) {
          lockReason = "previous_incomplete";
        }
      }

      const completedChallenges = progress?.completedChallenges ?? 0;
      const isCompleted = completedChallenges >= CHALLENGES_PER_LEVEL;

      return {
        levelNumber,
        isUnlocked,
        isCompleted,
        starsEarned: progress?.starsEarned ?? 0,
        maxStars: maxStarsPerLevel,
        hasBadge: progress?.hasBadge ?? false,
        lockReason,
      };
    });
  }, [levels, progressData, subscription]);

  const handleLevelSelect = useCallback(
    (levelNumber: number) => {
      navigate(`/play/${discipline}/${levelNumber}`);
    },
    [navigate, discipline]
  );

  if (!disciplineData || !discipline) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4">
        <p className="font-body text-body-lg text-neutral-600">
          Discipline not found
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoadingLevels) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-neutral-900 to-neutral-800">
        <div className="text-center">
          <div className="animate-spin w-16 h-16 border-4 border-white/20 border-t-sunshine-500 rounded-full mx-auto" />
          <p className="mt-4 font-display font-bold text-body-lg text-white/60">
            Loading {disciplineData.name}...
          </p>
        </div>
      </div>
    );
  }

  const levelProgress = buildLevelProgress();

  // Use the unified DisciplineLevelMap for all disciplines
  return (
    <div className="min-h-screen">
      <DisciplineLevelMap
        discipline={discipline}
        levels={levelProgress}
        onLevelSelect={handleLevelSelect}
        showBadgeCollection
      />
    </div>
  );
}

export default DisciplinePage;
